// Config-driven modules. Each module: list columns + form fields.
// field types: text, textarea, number, date, select, lookup, boolean

export const CUSTOMER_STATUS = ["Prospect", "Active", "On Hold", "Inactive", "Archived"];
export const PRIORITIES = ["Low", "Medium", "High", "Critical"];

export const MODULES = {
  customers: {
    label: "Customers", entity: "customers", singular: "Customer",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "name", label: "Customer" },
      { key: "customer_account_ref", label: "Account Ref" },
      { key: "main_contact", label: "Main Contact" },
      { key: "phone", label: "Phone" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      { key: "name", label: "Customer name", required: true },
      { key: "legal_name", label: "Legal name" },
      { key: "customer_account_ref", label: "Customer account reference" },
      { key: "status", label: "Account status", type: "select", options: CUSTOMER_STATUS, default: "Active" },
      { key: "main_contact", label: "Main contact" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Telephone" },
      { key: "accounts_contact", label: "Accounts contact name" },
      { key: "accounts_email", label: "Accounts email" },
      { key: "accounts_phone", label: "Accounts telephone" },
      { key: "billing_address", label: "Billing address", type: "textarea" },
      { key: "payment_terms", label: "Payment terms", default: "30 days" },
      { key: "po_required", label: "Purchase order required", type: "boolean" },
      { key: "account_owner", label: "Internal account owner" },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  sites: {
    label: "Sites", entity: "sites", singular: "Site",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "name", label: "Site" },
      { key: "customer_name", label: "Customer" },
      { key: "address", label: "Address" },
      { key: "compliance_status", label: "Compliance", type: "badge" },
    ],
    fields: [
      { key: "name", label: "Site name", required: true },
      { key: "customer_id", label: "Customer", type: "lookup", entity: "customers", labelKey: "name", mirror: "customer_name" },
      { key: "address", label: "Site address", type: "textarea" },
      { key: "eircode", label: "Eircode / postcode" },
      { key: "site_contact", label: "Site contact" },
      { key: "site_email", label: "Site contact email" },
      { key: "site_phone", label: "Site contact telephone" },
      { key: "site_notes", label: "Site notes / details", type: "textarea" },
      { key: "opening_hours", label: "Opening hours" },
      { key: "access_instructions", label: "Access instructions", type: "textarea" },
      { key: "site_risks", label: "Site risks", type: "textarea" },
      { key: "compliance_status", label: "Compliance status", type: "select", options: ["Compliant", "Action Required", "Overdue"], default: "Compliant" },
    ],
  },
  contracts: {
    label: "Contracts", entity: "contracts", singular: "Contract",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "name", label: "Contract" },
      { key: "contract_type", label: "Type" },
      { key: "customer_name", label: "Customer" },
      { key: "site_name", label: "Site" },
      { key: "end_date", label: "End", type: "date" },
      { key: "renewal_date", label: "Renewal", type: "date" },
      { key: "contract_value", label: "Value", type: "money" },
    ],
    fields: [
      { key: "name", label: "Contract name", required: true },
      { key: "contract_type", label: "Contract type", type: "select", options: ["One-Off", "Recurring"], default: "Recurring" },
      { key: "customer_id", label: "Customer", type: "lookup", entity: "customers", labelKey: "name", mirror: "customer_name" },
      { key: "site_id", label: "Site (1 contract per site)", type: "lookup", entity: "sites", labelKey: "name", mirror: "site_name" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "end_date", label: "End date", type: "date" },
      { key: "renewal_date", label: "Renewal date", type: "date" },
      { key: "contract_value", label: "Contract value", type: "number" },
      { key: "billing_frequency", label: "Billing frequency", type: "select", options: ["One-Off", "Monthly", "Quarterly", "Bi-Annual", "Annually"] },
      { key: "service_frequency", label: "Planned service frequency", type: "select", options: ["One-Off", "Monthly", "Quarterly", "Bi-Annual", "Annually"] },
      { key: "contract_owner", label: "Contract owner" },
      { key: "renewal_probability", label: "Renewal probability", type: "select", options: ["Low", "Medium", "High"] },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  },
  assets: {
    label: "Assets", entity: "assets", singular: "Asset",
    columns: [
      { key: "ref", label: "Asset No" },
      { key: "description", label: "Description" },
      { key: "site_name", label: "Site" },
      { key: "asset_category", label: "Category" },
      { key: "next_service_date", label: "Next Service", type: "date" },
      { key: "operational_status", label: "Status", type: "badge" },
    ],
    fields: [
      { key: "description", label: "Asset description", required: true },
      { key: "site_id", label: "Site", type: "lookup", entity: "sites", labelKey: "name", mirror: "site_name" },
      { key: "customer_name", label: "Customer" },
      { key: "asset_category", label: "Asset category" },
      { key: "manufacturer", label: "Manufacturer" },
      { key: "model", label: "Model" },
      { key: "serial_number", label: "Serial number" },
      { key: "installation_date", label: "Installation date", type: "date" },
      { key: "warranty_expiry", label: "Warranty expiry", type: "date" },
      { key: "service_frequency", label: "Service frequency", type: "select", options: ["One-Off", "Monthly", "Quarterly", "Bi-Annual", "Annually"] },
      { key: "next_service_date", label: "Next service date", type: "date" },
      { key: "condition", label: "Current condition", type: "select", options: ["Good", "Fair", "Poor"] },
      { key: "operational_status", label: "Operational status", type: "select", options: ["Operational", "Faulty", "Out of Service"], default: "Operational" },
      { key: "risk_rating", label: "Risk rating", type: "select", options: PRIORITIES },
    ],
    qr: true,
  },
  defects: {
    label: "Defects", entity: "defects", singular: "Defect",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "description", label: "Description" },
      { key: "site_name", label: "Site" },
      { key: "source_job_ref", label: "From Job" },
      { key: "risk_rating", label: "Risk", type: "badge" },
      { key: "status", label: "Status", type: "badge" },
      { key: "target_date", label: "Target", type: "date" },
    ],
    fields: [
      { key: "description", label: "Description", required: true, type: "textarea" },
      { key: "customer_name", label: "Customer" },
      { key: "site_name", label: "Site" },
      { key: "risk_rating", label: "Risk rating", type: "select", options: PRIORITIES, default: "Medium" },
      { key: "severity", label: "Severity", type: "select", options: ["Minor", "Moderate", "Major"] },
      { key: "status", label: "Status", type: "select", options: ["Identified", "Under Review", "Immediate Action Required", "Quotation Required", "Quotation Issued", "Awaiting Customer Approval", "Approved", "Scheduled", "Completed", "Verified", "Closed", "Rejected by Customer"], default: "Identified" },
      { key: "immediate_action", label: "Immediate action taken", type: "textarea" },
      { key: "recommended_action", label: "Recommended action", type: "textarea" },
      { key: "responsible_person", label: "Responsible person" },
      { key: "target_date", label: "Target date", type: "date" },
    ],
  },
  quotations: {
    label: "Quotations", entity: "quotations", singular: "Quotation",
    columns: [
      { key: "ref", label: "Ref", w: "w-40", tagJob: true },
      { key: "description", label: "Scope", wrap: true },
    ],
    fields: [
      { key: "description", label: "Scope of work", required: true, type: "textarea" },
      { key: "customer_name", label: "Customer" },
      { key: "site_name", label: "Site" },
      { key: "line_items", label: "Line items (optional — auto-fills selling price)", type: "lineitems" },
      { key: "labour", label: "Labour cost", type: "number" },
      { key: "materials", label: "Materials cost", type: "number" },
      { key: "internal_cost", label: "Internal estimated cost", type: "number" },
      { key: "selling_price", label: "Selling price", type: "number" },
      { key: "gross_margin", label: "Gross margin % (auto)", type: "computed",
        compute: (v) => {
          const sp = parseFloat(v.selling_price) || 0;
          const lab = parseFloat(v.labour) || 0, mat = parseFloat(v.materials) || 0;
          const cost = lab || mat ? lab + mat : parseFloat(v.internal_cost) || 0;
          return sp > 0 ? Number((((sp - cost) / sp) * 100).toFixed(1)) : "";
        } },
      { key: "status", label: "Status", type: "select", options: ["Draft", "Internal Review", "Approved Internally", "Issued", "Follow-up Due", "Customer Query", "Accepted", "Rejected", "Expired", "Converted to Job"], default: "Draft" },
      { key: "validity_date", label: "Validity date", type: "date" },
      { key: "follow_up_date", label: "Follow-up date", type: "date" },
    ],
  },
  stock: {
    label: "Stock", entity: "stock", singular: "Stock Item",
    columns: [
      { key: "ref", label: "Code" },
      { key: "name", label: "Item" },
      { key: "category", label: "Category" },
      { key: "quantity_available", label: "Qty" },
      { key: "minimum_level", label: "Min" },
      { key: "location", label: "Location" },
    ],
    fields: [
      { key: "name", label: "Description", required: true },
      { key: "category", label: "Category" },
      { key: "supplier", label: "Supplier" },
      { key: "cost_price", label: "Cost price", type: "number" },
      { key: "selling_price", label: "Standard selling price", type: "number" },
      { key: "quantity_available", label: "Quantity available", type: "number" },
      { key: "minimum_level", label: "Minimum level", type: "number" },
      { key: "reorder_level", label: "Reorder level", type: "number" },
      { key: "location", label: "Storage location" },
    ],
  },
  suppliers: {
    label: "Suppliers", entity: "suppliers", singular: "Supplier",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "name", label: "Supplier" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "lead_time", label: "Lead Time" },
    ],
    fields: [
      { key: "name", label: "Supplier name", required: true },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "address", label: "Address", type: "textarea" },
      { key: "product_categories", label: "Product categories" },
      { key: "payment_terms", label: "Payment terms" },
      { key: "lead_time", label: "Expected lead time" },
    ],
  },
  "purchase-orders": {
    label: "Purchasing", entity: "purchase-orders", singular: "Purchase Order",
    columns: [
      { key: "ref", label: "PO No" },
      { key: "supplier", label: "Supplier" },
      { key: "order_date", label: "Order Date", type: "date" },
      { key: "total_cost", label: "Total", type: "money" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      { key: "supplier", label: "Supplier", required: true },
      { key: "order_date", label: "Order date", type: "date" },
      { key: "requested_delivery_date", label: "Requested delivery date", type: "date" },
      { key: "items", label: "Items", type: "textarea" },
      { key: "total_cost", label: "Total cost", type: "number" },
      { key: "related_job_ref", label: "Related job" },
      { key: "status", label: "Approval status", type: "select", options: ["Draft", "Pending Approval", "Approved", "Ordered", "Received"], default: "Draft" },
    ],
  },
  employees: {
    label: "People", entity: "employees", singular: "Employee",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "name", label: "Name" },
      { key: "role", label: "Role" },
      { key: "department", label: "Department" },
      { key: "qualification_expiry", label: "Qual Expiry", type: "date" },
    ],
    fields: [
      { key: "name", label: "Employee name", required: true },
      { key: "role", label: "Role" },
      { key: "department", label: "Department" },
      { key: "employment_status", label: "Employment status", type: "select", options: ["Full time", "Part time", "Contractor"] },
      { key: "skills", label: "Skills" },
      { key: "qualifications", label: "Qualifications" },
      { key: "qualification_expiry", label: "Qualification expiry", type: "date" },
      { key: "assigned_vehicle", label: "Assigned vehicle" },
    ],
  },
  vehicles: {
    label: "Vehicles & Equipment", entity: "vehicles", singular: "Vehicle",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "name", label: "Vehicle / Equipment" },
      { key: "registration", label: "Reg" },
      { key: "assigned_employee", label: "Assigned To" },
      { key: "cvrt_expiry", label: "CVRT / MOT", type: "date" },
    ],
    fields: [
      { key: "name", label: "Name", required: true },
      { key: "registration", label: "Registration / asset number" },
      { key: "type", label: "Type", type: "select", options: ["Van", "Car", "Tool", "Testing Equipment"] },
      { key: "assigned_employee", label: "Assigned employee" },
      { key: "service_date", label: "Service date", type: "date" },
      { key: "insurance_expiry", label: "Insurance expiry", type: "date" },
      { key: "cvrt_expiry", label: "CVRT / MOT expiry", type: "date" },
    ],
  },
  projects: {
    label: "Projects", entity: "projects", singular: "Project",
    columns: [
      { key: "ref", label: "Ref" },
      { key: "name", label: "Project" },
      { key: "customer_name", label: "Customer" },
      { key: "project_manager", label: "PM" },
      { key: "percent_complete", label: "% Complete" },
      { key: "status", label: "Status", type: "badge" },
    ],
    fields: [
      { key: "name", label: "Project name", required: true },
      { key: "customer_name", label: "Customer" },
      { key: "site_name", label: "Site" },
      { key: "project_manager", label: "Project manager" },
      { key: "scope", label: "Scope", type: "textarea" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "target_completion_date", label: "Target completion date", type: "date" },
      { key: "contract_value", label: "Contract value", type: "number" },
      { key: "budget_cost", label: "Budgeted cost", type: "number" },
      { key: "percent_complete", label: "Percentage complete", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Planning", "In Progress", "On Hold", "Completed"], default: "Planning" },
    ],
  },
};
